"use client"

import { useState, useEffect } from "react"
import { useNavigate, Link } from "react-router-dom"
import { Helmet } from "react-helmet"
import { authHelpers } from "../../firebaseClient"
import AuthSwitch from "./components/AuthSwitch"
import Icon from "../../components/AppIcon"
import { FloatingPaths } from "../../components/ui/FloatingPaths"
import { getPostLoginPath } from "../../utils/authRedirect"
import { markInvitationOpened } from "../../utils/invitationHelpers"

const AuthenticationPortal = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Fires once per page load regardless of auth state — tracks that the
    // invite link was actually opened, distinct from whether the visitor
    // goes on to complete signup (that's acceptInvitation, in getPostLoginPath).
    const invitationId = new URLSearchParams(window.location.search).get("invite")
    if (invitationId) markInvitationOpened(invitationId)
  }, [])

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const { session, error } = await authHelpers.getSession()

        if (error) {
          console.error("Error checking auth status:", error)
          setIsLoading(false)
          return
        }

      if (session) {
          console.log("User already authenticated, redirecting to dashboard")
          const redirectPath = await getPostLoginPath(session.user.uid, session.user.email)
          navigate(redirectPath, { replace: true })
          return
        }
      } catch (error) {
        console.error("Error checking auth status:", error)
      }

      setIsLoading(false)
    }

    checkAuthStatus()
  }, [navigate])

  const handleAuthSuccess = async () => {
    console.log("Auth success triggered, verifying session...")

    try {
      // Firebase auth is instant, no delay needed
      const { session, error } = await authHelpers.getSession()

      if (error) {
        console.error("Error verifying session after auth:", error)
        alert("Authentication succeeded but session verification failed. Please try logging in again.")
        return
      }

      if (session) {
        console.log("Session verified, user:", session.user.email)
        const redirectPath = await getPostLoginPath(session.user.uid, session.user.email)
        navigate(redirectPath, { replace: true })
      } else {
        console.error("No session found after successful auth")
        alert("Authentication succeeded but no session was created. Please try logging in again.")
      }
    } catch (error) {
      console.error("Error in handleAuthSuccess:", error)
      alert("An error occurred after authentication. Please try logging in again.")
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Checking authentication...</span>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Sign In - Nesolearn Platform</title>
        <meta
          name="description"
          content="Sign in to Nesolearn Platform - Access AI-powered courses and track your learning progress"
        />
      </Helmet>

      <div className="min-h-screen relative" style={{ backgroundColor: "#1b1b1b" }}>
        <div className="absolute inset-0 pointer-events-none text-white">
          <FloatingPaths position={1} />
          <FloatingPaths position={-1} />
        </div>

        <div className="absolute top-5 left-4 sm:left-8 z-10 flex flex-row items-center gap-3">
          <Link to="/home-landing-page" aria-label="Nesolearn Home">
            <img
              src="https://pub-b365ff8e20e448c3a7257b35173d9a38.r2.dev/NESOL_2D_Logo_-_Black_and_White-removebg-preview.png"
              alt="Nesolearn"
              className="w-9 h-9 object-contain brightness-0 invert"
            />
          </Link>

          <Link
            to="/home-landing-page"
            aria-label="Go to home page"
            className="flex items-center justify-center w-9 h-9 rounded-md bg-white/10 text-white hover:bg-white/20 transition-smooth backdrop-blur-sm"
          >
            <Icon name="Home" size={16} />
          </Link>
        </div>
        <AuthSwitch onSuccess={handleAuthSuccess} />
      </div>
    </>
  )
}

export default AuthenticationPortal